import React, { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import Sidebar from './components/layout/Sidebar';
import Topbar  from './components/layout/Topbar';
import LoginPage    from './pages/LoginPage';
import Dashboard    from './pages/Dashboard';
import CustomersPage from './pages/CustomersPage';
import ItemsPage    from './pages/ItemsPage';
import InventoryPage from './pages/InventoryPage';
import SalesPage    from './pages/SalesPage';
import ReportsPage  from './pages/ReportsPage';
import { SuppliersPage, PurchasePage, UsersPage, BankSettingsPage } from './pages/OtherPages';
import BranchesPage from './pages/BranchesPage';
import AuditLogsPage from './pages/AuditLogsPage';
import { SKUMasterPage } from './pages/SKUMasterPage';
import TagViewPage from './pages/TagViewPage';
import WebsiteCmsPage from './pages/WebsiteCmsPage';
import WebsiteOrdersPage from './pages/WebsiteOrdersPage';
import CouponsPage from './pages/CouponsPage';

function ProtectedLayout() {
  const { user } = useAuth();
  // Sidebar expanded state: persisted to localStorage
  const [sidebarExpanded, setSidebarExpanded] = useState(() => {
    try { return localStorage.getItem('sb_expanded') === '1'; } catch { return false; }
  });

  const toggleSidebar = () => {
    setSidebarExpanded(prev => {
      const nxt = !prev;
      try { localStorage.setItem('sb_expanded', nxt ? '1' : '0'); } catch {}
      return nxt;
    });
  };

  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className={`app-layout ${sidebarExpanded ? 'sidebar-open' : 'sidebar-collapsed'}`}>
      <Sidebar isExpanded={sidebarExpanded} onToggle={toggleSidebar} />
      <div className="main-wrapper">
        <Topbar isExpanded={sidebarExpanded} onToggleSidebar={toggleSidebar} />
        <main className="main-content">
          <Routes>
            <Route path="/"          element={<Dashboard />} />
            <Route path="/sales"     element={<SalesPage />} />
            <Route path="/purchase"  element={<PurchasePage />} />
            <Route path="/inventory" element={<InventoryPage />} />
            <Route path="/customers" element={<CustomersPage />} />
            <Route path="/suppliers" element={<SuppliersPage />} />
            <Route path="/items"     element={<ItemsPage />} />
            <Route path="/website-cms" element={<WebsiteCmsPage />} />
            <Route path="/website-orders" element={<WebsiteOrdersPage />} />
            <Route path="/coupons" element={<CouponsPage />} />
            <Route path="/reports"   element={<ReportsPage />} />
            <Route path="/gst"       element={<ReportsPage />} />
            <Route path="/users"     element={<UsersPage />} />
            <Route path="/bank-settings" element={<BankSettingsPage />} />
            <Route path="/branches"  element={<BranchesPage />} />
            <Route path="/sku-master" element={<SKUMasterPage />} />
            {(user?.username?.toLowerCase() === 'mantracode' || user?.roleName === 'SuperAdmin') && <Route path="/audit-logs" element={<AuditLogsPage />} />}
            <Route path="*"          element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function AppRoutes() {
  const { user, loading } = useAuth();

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh', gap:12, color:'var(--text-muted)' }}>
      <div className="spinner" />
      <span style={{ fontSize: '0.875rem', fontFamily: 'var(--font)' }}>Loading SIMS...</span>
    </div>
  );

  return (
    <Routes>
      {/* Public tag-scan page — no login, opened from a jewellery tag QR */}
      <Route path="/t/:sku" element={<TagViewPage />} />
      <Route path="/tag/:sku" element={<TagViewPage />} />
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/*" element={<ProtectedLayout />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              fontFamily: 'var(--font)',
              fontSize: '0.875rem',
              borderRadius: '10px',
              boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
              border: '1px solid var(--border)',
            },
            success: { iconTheme: { primary: '#10b981', secondary: '#fff' } },
            error:   { iconTheme: { primary: '#ef4444', secondary: '#fff' } },
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}
